import { Study } from 'interfaces/study';
import { compact, filter, map } from 'lodash';
import moment from 'moment';
import { filterSearchOptions } from 'services/matchSort';

interface StudySearchFilters {
  text?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

interface SearchableStudy extends Study {
  searchText: string;
}

export interface FilteredStudyResult {
  matchedFields: string[];
  study: Study;
}

function normalizeStudyIdentifier(value?: string | null) {
  return value ? value.replace(/[^a-zA-Z0-9]/g, '') : '';
}

function getStudySearchText(study: Study) {
  return [
    study.name,
    study.description,
    study.externalStudyId,
    study.id,
    normalizeStudyIdentifier(study.externalStudyId),
    normalizeStudyIdentifier(study.id),
  ]
    .filter(Boolean)
    .join(' ');
}

function getMatchedFields(study: Study, text?: string) {
  const trimmedText = text?.trim();

  if (!trimmedText) {
    return [];
  }

  const searchableFields = [
    { label: 'Name', values: [study.name] },
    { label: 'Description', values: [study.description] },
    { label: 'Study ID', values: [study.externalStudyId, normalizeStudyIdentifier(study.externalStudyId)] },
    { label: 'UUID', values: [study.id, normalizeStudyIdentifier(study.id)] },
  ];

  return compact(
    map(searchableFields, ({ label, values }) => {
      const matches = filterSearchOptions(
        ['value'],
        compact(map(values, (value) => (value ? { value } : null))),
        trimmedText
      );

      return matches.length ? label : null;
    })
  );
}

export function filterStudies(studies: Study[] | undefined, { text, startDate, endDate }: StudySearchFilters = {}) {
  const allStudies = studies || [];
  const trimmedText = text?.trim();
  const searchableStudies: SearchableStudy[] = map(allStudies, (study) => ({
    ...study,
    searchText: getStudySearchText(study),
  }));

  const textFilteredStudies = trimmedText
    ? filterSearchOptions(['searchText'], searchableStudies, trimmedText)
    : searchableStudies;

  return map(
    filter(textFilteredStudies, (study) => {
      const isStartDateMatch = !startDate || (study.date && moment(startDate).isBefore(study.date));
      const isEndDateMatch = !endDate || (study.date && moment(endDate).isAfter(study.date));

      return isStartDateMatch && isEndDateMatch;
    }),
    ({ searchText: _searchText, ...study }): FilteredStudyResult => ({
      matchedFields: getMatchedFields(study, trimmedText),
      study,
    })
  );
}
